import type { DataTableAction, DataTableExpandAction, DataTableExpansion } from './types';

/** Options for `expandAction`. The expansion state itself is the required argument. */
export interface ExpandActionOptions {
    /** Names the thing being expanded, e.g. "Invoices". Defaults to "table". */
    label?: string;
    /** Stable React key. Defaults to `'expand'`. */
    key?: string;
    className?: string;
}

/**
 * Places the expand / full-screen toggle as an ordinary member of the
 * `actions` array - the same factory-returning-data pattern as the column
 * presets, so the toggle has no special placement mechanism of its own.
 * Order in the array is order in the toolbar's end group.
 *
 * Requiring the `DataTableExpansion` object here is the type-level half of
 * the coupling: an entry cannot exist without `useDataTableExpansion` state.
 * The other half - passing that same object to `<DataTable expansion>` so the
 * overlay actually renders - is not cleanly expressible in the types (it
 * would take value-dependent inference over the array's contents), so
 * `DataTable` instead drops the entry when its `expansion` prop is absent:
 * a missing toggle at build time, never a dead one in production.
 */
export function expandAction(
    expansion: DataTableExpansion,
    options: ExpandActionOptions = {}
): DataTableExpandAction {
    return { kind: 'expand', expansion, ...options };
}

/** Discriminates the expand entry from plain buttons - buttons carry no `kind`. */
export function isExpandAction(action: DataTableAction): action is DataTableExpandAction {
    return 'kind' in action && action.kind === 'expand';
}
